import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Gavel, Snowflake, Award, Sparkles, CheckCheck } from 'lucide-react';
import { engagement } from '../services/api';
import { toast } from '../hooks/use-toast';

const ICONS = {
  auction_won: Gavel,
  auction_sold: Gavel,
  auction_outbid: Gavel,
  auction_expired: Gavel,
  freeze: Snowflake,
  points: Award,
};

function iconFor(type) {
  return ICONS[type] || Sparkles;
}

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const CHECKOUT_TYPES = new Set(['auction_won', 'auction_cascade_won', 'auction_pending_accept']);
const checkoutLink = (n) => (CHECKOUT_TYPES.has(n.type) && n.auctionId) ? `/auction/${n.auctionId}/checkout` : null;

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const seenIdsRef = useRef(null); // null = first load not yet done

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await engagement.notifications();
      const list = Array.isArray(data) ? data : [];
      // Toast any brand-new unread notifications we haven't seen before.
      // Skip toasting on the very first load so we don't spam on page refresh.
      if (seenIdsRef.current === null) {
        seenIdsRef.current = new Set(list.map((n) => n.id));
      } else {
        const seen = seenIdsRef.current;
        const fresh = list.filter((n) => !seen.has(n.id) && !n.read);
        // Show newest last so the visible toast is the most recent
        for (const n of fresh.slice().reverse()) {
          toast({ title: n.title || 'New notification', description: n.body || '' });
        }
        for (const n of list) seen.add(n.id);
      }
      setItems(list);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + polling every 30s
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  // Refresh when opening
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const unread = items.filter((n) => !n.read).length;

  const markOne = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try { await engagement.readNotif(id); } catch (e) { /* ignore */ }
  };

  const markAll = async () => {
    const unreadIds = items.filter((n) => !n.read).map((n) => n.id);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    await Promise.all(unreadIds.map((id) => engagement.readNotif(id).catch(() => null)));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="notifications-bell-button"
        onClick={() => setOpen((v) => !v)}
        className="relative text-gray-300 hover:text-white p-1 rounded-full transition-colors"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border border-black"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-dropdown"
          className="absolute right-0 mt-2 w-[340px] sm:w-[380px] bg-[#0f0f0f] border border-[#1f1f1f] rounded-xl shadow-2xl overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1f1f1f]">
            <div className="text-sm font-semibold">Notifications</div>
            {unread > 0 && (
              <button
                data-testid="notifications-mark-all-read"
                onClick={markAll}
                className="text-xs text-green-400 hover:text-green-300 inline-flex items-center gap-1"
              >
                <CheckCheck size={13} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500 text-center">Loading…</div>
            ) : items.length === 0 ? (
              <div data-testid="notifications-empty" className="px-4 py-8 text-sm text-gray-500 text-center">
                You’re all caught up.
              </div>
            ) : (
              items.map((n) => {
                const Icon = iconFor(n.type);
                return (
                  <div
                    role="button" tabIndex={0}
                    key={n.id}
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => !n.read && markOne(n.id)}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-[#1a1a1a] hover:bg-white/5 transition-colors ${!n.read ? 'bg-green-600/5' : ''}`}
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${!n.read ? 'bg-green-600/20 text-green-400' : 'bg-white/5 text-gray-400'}`}>
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        <div className={`text-sm font-medium truncate ${!n.read ? 'text-white' : 'text-gray-300'}`}>{n.title}</div>
                        {!n.read && <span className="mt-1 w-2 h-2 rounded-full bg-green-500 shrink-0" />}
                      </div>
                      {n.body && (
                        <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{n.body}</div>
                      )}
                      {checkoutLink(n) && (
                        <Link to={checkoutLink(n)} onClick={(e) => { e.stopPropagation(); if (!n.read) markOne(n.id); setOpen(false); }}
                          data-testid={`notification-checkout-link-${n.id}`}
                          className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-600 hover:bg-green-500 text-white text-[11px] font-semibold">
                          Complete checkout →
                        </Link>
                      )}
                      <div className="text-[11px] text-gray-500 mt-1">{timeAgo(n.createdAt)}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
